import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Scan, Upload, Zap, Brain, Camera } from 'lucide-react';
import CameraView from '../components/FoodScanner/CameraView';
import NutritionOverlay from '../components/FoodScanner/NutritionOverlay';
import GlassCard from '../components/UI/GlassCard';
import FloatingElement from '../components/UI/FloatingElement';
import { useFoodAnalysis } from '../contexts/FoodAnalysisContext';

const FoodScanner: React.FC = () => {
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const { analyzedFoods, isModelLoading } = useFoodAnalysis();

  const handleCapture = (imageSrc: string) => {
    setCapturedImage(imageSrc);
    setIsAnalyzing(true);
    
    // Simulate analysis time
    setTimeout(() => {
      setIsAnalyzing(false);
    }, 3000);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageSrc = e.target?.result as string;
        handleCapture(imageSrc);
      };
      reader.readAsDataURL(file);
    }
  };

  if (isModelLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <GlassCard className="p-12 text-center">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 mx-auto mb-6"
          >
            <Brain className="w-16 h-16 text-purple-400" />
          </motion.div>
          <h2 className="text-2xl font-bold text-white mb-4">Loading AI Model</h2>
          <p className="text-purple-300">Preparing advanced food recognition capabilities...</p>
        </GlassCard>
      </div>
    );
  }

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <FloatingElement delay={0}>
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
              <Scan className="w-10 h-10 text-white" />
            </div>
          </FloatingElement>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            AI Food <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
              Scanner
            </span>
          </h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto mb-8">
            Point your camera at any food item for instant AI-powered nutritional analysis, 
            ingredient detection, and health insights with real-time 3D visualization.
          </p>
          
          {/* Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <FloatingElement delay={0.2}>
              <GlassCard className="p-6 text-center">
                <Zap className="w-8 h-8 text-yellow-400 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Instant Recognition</h3>
                <p className="text-purple-200 text-sm">Real-time food identification with 95%+ accuracy</p>
              </GlassCard>
            </FloatingElement>
            <FloatingElement delay={0.4}>
              <GlassCard className="p-6 text-center">
                <Brain className="w-8 h-8 text-purple-400 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">Multi-Item Detection</h3>
                <p className="text-purple-200 text-sm">Analyze multiple foods in a single frame</p>
              </GlassCard>
            </FloatingElement>
            <FloatingElement delay={0.6}>
              <GlassCard className="p-6 text-center">
                <Camera className="w-8 h-8 text-cyan-400 mx-auto mb-3" />
                <h3 className="text-white font-semibold mb-2">3D Visualization</h3>
                <p className="text-purple-200 text-sm">Interactive 3D models of detected foods</p>
              </GlassCard>
            </FloatingElement>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Camera/Upload Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <CameraView onCapture={handleCapture} isAnalyzing={isAnalyzing} />
            
            {/* Upload Option */}
            <div className="mt-6">
              <GlassCard className="p-6">
                <div className="text-center">
                  <Upload className="w-8 h-8 text-purple-400 mx-auto mb-3" />
                  <h3 className="text-white font-semibold mb-3">Or Upload an Image</h3>
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                    <span className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-6 py-3 rounded-full font-medium hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 inline-block">
                      Choose File
                    </span>
                  </label>
                </div>
              </GlassCard>
            </div>
          </motion.div>

          {/* Results Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            {analyzedFoods.length > 0 ? (
              <div>
                <h2 className="text-2xl font-bold text-white mb-6 flex items-center">
                  <Zap className="w-6 h-6 text-yellow-400 mr-2" />
                  Analysis Results
                </h2>
                <NutritionOverlay foods={analyzedFoods} />
              </div>
            ) : (
              <GlassCard className="p-12 text-center h-full flex items-center justify-center">
                <div>
                  <motion.div
                    animate={{ 
                      scale: [1, 1.1, 1],
                      opacity: [0.5, 1, 0.5]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                    className="w-24 h-24 mx-auto mb-6 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center border-2 border-purple-500/30"
                  >
                    <Scan className="w-12 h-12 text-purple-400" />
                  </motion.div>
                  <h3 className="text-xl font-semibold text-white mb-3">Ready to Scan</h3>
                  <p className="text-purple-200">
                    Capture or upload a food image to see detailed nutritional analysis, 
                    ingredient breakdown, and health insights powered by AI.
                  </p>
                </div>
              </GlassCard>
            )}
          </motion.div>
        </div>

        {/* Captured Image Preview */}
        {capturedImage && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-12"
          >
            <GlassCard className="p-6">
              <h3 className="text-xl font-bold text-white mb-4">Captured Image</h3>
              <img
                src={capturedImage}
                alt="Captured food"
                className="w-full max-w-md mx-auto rounded-xl"
              />
            </GlassCard>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FoodScanner;